import React from "react";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/core";
import { get, map, toUpper, size, filter, includes } from "lodash";
import clsx from "clsx";

import Grid from "@material-ui/core/Grid";

import SchedulerException from "../SchedulerException/SchedulerException";
import SchedulerCalendarTimeSlot from "../SchedulerCalendarTimeSlot/SchedulerCalendarTimeSlot";

import {
  getDayVisitorsCount,
  getExceptions,
  getOverlapExceptionsCount,
  getTimeSlotHeight,
  isOccurrenceInTimeSlot,
} from "../../../utils/scheduler";
import { isToday } from "../../../utils/date";

import { calendarViews } from "../../../constants/common";

import images from "../../../assets/img/images";
import colors from "../../../assets/style/colors";
import SchedulerOccurrenceWeekly from "../SchedulerOccurrence/SchedulerOccurrenceWeekly";
import SchedulerOccurrenceDraggable from "../SchedulerOccurrence/SchedulerOccurrenceDraggable";

const styles = {
  container: {
    border: `1px solid ${colors.veryLighPink}`,
    borderLeft: "none",
    "@media print": {
      width: "40mm",
      minWidth: "40mm",
      maxWidth: "40mm",
    },
  },
  dateSection: {
    backgroundColor: colors.lightGrey,
    padding: "12px 0px",
  },
  todayDateSection: {
    backgroundColor: colors.calendarLightBlue,
  },
  statsBar: {
    height: "22px",
    lineHeight: "15px",
    padding: "0px 5px",
    border: `1px solid ${colors.veryLighPink}`,
    borderRight: "none",
    borderLeft: "none",
  },
  timeSlotSeparator: {
    height: "26px",
    marginBottom: "2px",
    backgroundColor: colors.veryLighPink,
  },
};

const SchedulerCalendarWeeklyDay = (props) => {
  const {
    classes,
    date,
    occurrencesPerDay,
    occurrences,
    timeSlots,
    refresh,
    showCustomTimeSlots,
    end
  } = props;

  const days = {
    0: I18n.get("common_sun"),
    1: I18n.get("common_mon"),
    2: I18n.get("common_tue"),
    3: I18n.get("common_wed"),
    4: I18n.get("common_thu"),
    5: I18n.get("common_fri"),
    6: I18n.get("common_sat"),
  };

  const isValidDate = date && typeof date.getMonth === "function";

  const exceptions = getExceptions(occurrences, timeSlots);
  const timeSlotHeights = map(timeSlots, (slot) =>
    getTimeSlotHeight(occurrencesPerDay, slot, calendarViews.weekly)
  );

  const noExceptionWidth = 182;
  const columnWidth =
    size(exceptions) > 0 && showCustomTimeSlots
      ? noExceptionWidth + 60 * getOverlapExceptionsCount(exceptions)
      : noExceptionWidth;

  const filteredOccurrences = showCustomTimeSlots
    ? occurrences
    : filter(
        occurrences,
        (occ) =>
          !includes(
            map(exceptions, (e) => JSON.stringify(e)),
            JSON.stringify(occ)
          )
      );

  return (
    <Grid
      item
      data-test="scheduler-calendar-day-component"
      className={classes.container}
      style={{
        width: columnWidth,
        maxWidth: columnWidth,
        minWidth: columnWidth,
      }}
    >
      <Grid container direction="column">
        <Grid
          item
          container
          direction="column"
          className={clsx(classes.dateSection, {
            [classes.todayDateSection]: isValidDate && isToday(date),
          })}
        >
          <Grid
            item
            className="text-54 text-center"
            data-test="scheduler-calendar-day-date"
          >
            {isValidDate ? date.getDate() : ""}
          </Grid>
          <Grid
            item
            className="text-87 text-center"
            data-test="scheduler-calendar-day-day"
          >
            {isValidDate ? toUpper(get(days, [date.getDay()])) : ""}
          </Grid>
        </Grid>
        <Grid item className={classes.statsBar}>
          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className="height-100"
          >
            <Grid item className="flexCenter" style={{ width: (parseInt(I18n.get("custom_css")) + (columnWidth - 190)).toString() + "px" }}>
              <img
                src={images.visitIcon}
                alt="clock"
                style={{ marginRight: "5px" }}
              />
              <span>{size(occurrences) +" ("+size(exceptions) +" "+ I18n.get("custom") +") "}</span> 
            </Grid>
            <Grid item xs className="flexCenter">
              <img
                src={images.pedestrianIcon}
                alt="human"
                style={{ marginRight: "5px" }}
              />
              <span>{" "+ getDayVisitorsCount(filteredOccurrences)+ " (" + getDayVisitorsCount(exceptions) + ")"}</span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item style={{ position: "relative" }}>
          <Grid container>
            <Grid item xs>
              {map(timeSlots, (slot, index) => (
                <React.Fragment key={index}>
                  <SchedulerCalendarTimeSlot
                    date={date}
                    slot={slot}
                    timeSlots={timeSlots}
                    width={noExceptionWidth}
                    height={get(timeSlotHeights, [index])}
                    refresh={refresh}
                  >
                    {map(
                      filter(occurrences, (item) =>
                        isOccurrenceInTimeSlot(item, slot)
                      ),
                      (item, index) => (
                        <SchedulerOccurrenceDraggable
                          key={index}
                          group={item}
                          refresh={refresh}
                        >
                          <SchedulerOccurrenceWeekly
                            group={item}
                            timeSlots={timeSlots}
                            refresh={refresh}
                            data-test="scheduler-occurrence-item"
                          />
                        </SchedulerOccurrenceDraggable>
                      )
                    )}
                  </SchedulerCalendarTimeSlot>
                  <Grid item className={classes.timeSlotSeparator} />
                </React.Fragment>
              ))}
            </Grid>
            {size(exceptions) > 0 && showCustomTimeSlots && (
              <Grid item>
                {map(exceptions, (e, index) => (
                  <SchedulerException
                    key={index}
                    group={e}
                    refresh={refresh}
                    timeSlots={timeSlots}
                    timeSlotHeights={timeSlotHeights}
                    calendarView={calendarViews.weekly}
                    exceptions={exceptions}
                    index={index}
                    end={end}
                    type={"weekly"}
                  />
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(SchedulerCalendarWeeklyDay);
